import { create } from 'zustand';
import { Suspension, SuspensionType } from '../types/suspension';
import { collection, doc, setDoc, deleteDoc, query, where, onSnapshot, updateDoc, getDocs } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';

type SuspensionState = {
  suspensions: Record<string, Suspension>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  initializeListener: () => () => void;
  createSuspension: (suspension: Omit<Suspension, 'id'>) => Promise<void>;
  updateSuspension: (suspensionId: string, updates: Partial<Suspension>) => Promise<void>;
  removeSuspension: (suspensionId: string) => Promise<void>;
  checkServiceSuspension: (type: SuspensionType, hotelId?: string) => Promise<boolean>;
};

export const useSuspensionStore = create<SuspensionState>((set, get) => ({
  suspensions: {},
  loading: false,
  error: null,
  initialized: false,

  initializeListener: () => {
    if (get().initialized) return () => {};
    
    const unsubscribe = onSnapshot(
      collection(firestore, 'Suspensions'),
      (snapshot) => {
        const suspensions: Record<string, Suspension> = {};
        snapshot.forEach((doc) => {
          suspensions[doc.id] = {
            ...doc.data(),
            id: doc.id
          } as Suspension;
        });
        set({ suspensions, initialized: true });
      },
      (error) => {
        console.error('Error in suspensions listener:', error);
        set({ error: error.message, initialized: false });
      }
    );

    return unsubscribe;
  },

  createSuspension: async (suspensionData) => {
    set({ loading: true, error: null });
    try {
      const suspensionRef = doc(collection(firestore, 'Suspensions'));
      const suspension: Suspension = {
        ...suspensionData,
        id: suspensionRef.id,
        isActive: true
      };
      
      await setDoc(suspensionRef, suspension);
      
      set((state) => ({
        suspensions: {
          ...state.suspensions,
          [suspension.id]: suspension
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error creating suspension:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateSuspension: async (suspensionId, updates) => {
    set({ loading: true, error: null });
    try {
      await updateDoc(doc(firestore, 'Suspensions', suspensionId), updates);
      
      set((state) => ({
        suspensions: {
          ...state.suspensions,
          [suspensionId]: {
            ...state.suspensions[suspensionId],
            ...updates
          }
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error updating suspension:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  removeSuspension: async (suspensionId) => {
    set({ loading: true, error: null });
    try {
      await deleteDoc(doc(firestore, 'Suspensions', suspensionId));
      
      set((state) => {
        const newSuspensions = { ...state.suspensions };
        delete newSuspensions[suspensionId];
        return { suspensions: newSuspensions, loading: false };
      });
    } catch (error: any) {
      console.error('Error removing suspension:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  checkServiceSuspension: async (type: SuspensionType, hotelId?: string) => {
    try {
      const q = query(
        collection(firestore, 'Suspensions'),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const now = Date.now();
      
      return snapshot.docs.some(doc => {
        const suspension = doc.data() as Suspension;
        const isExpired = suspension.expiresAt !== null && suspension.expiresAt < now;
        
        if (isExpired) return false;

        if (type === 'specific_hotel' && hotelId) {
          return suspension.type === 'all_accommodation' || 
                 (suspension.type === 'specific_hotel' && suspension.hotelId === hotelId);
        }

        return suspension.type === type;
      });
    } catch (error) {
      console.error('Error checking service suspension:', error);
      return false;
    }
  }
}));